import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { SMLAnalytics } from '../../analytics/sml/analytics';

/**
 * The funnel events of the trial upsell (spec 2026-10-13 §8). `SMLAnalytics` sends them only after
 * the user accepted the cookies. Each event carries the surface (a value of `UPSELL_SURFACES`),
 * the count of watermarked files and the plan.
 */
export const UPSELL_EVENTS = {
  shown: 'upsell_shown',
  clicked: 'upsell_clicked',
  continued: 'upsell_continued_with_watermark',
  dismissed: 'upsell_dismissed',
} as const;

export interface UpsellEventFacts {
  surface: UpsellSurface;
  files: number;
  plan: Music24PlanTier;
}

type UpsellEvent = (typeof UPSELL_EVENTS)[keyof typeof UPSELL_EVENTS];

function track(event: UpsellEvent, facts: UpsellEventFacts): void {
  SMLAnalytics.track(event, { surface: facts.surface, files: facts.files, plan: facts.plan }).catch(console.error);
}

/** A surface came on screen. An always-on surface sends it one time for each page view. */
export function trackUpsellShown(facts: UpsellEventFacts): void {
  track(UPSELL_EVENTS.shown, facts);
}

/** "Remove the watermark" (or the pill, the flag) was pressed on a surface. */
export function trackUpsellClicked(facts: UpsellEventFacts): void {
  track(UPSELL_EVENTS.clicked, facts);
}

/** The user went on with the watermarked file (the second row of the choice, "Make the preview"). */
export function trackUpsellContinued(facts: UpsellEventFacts): void {
  track(UPSELL_EVENTS.continued, facts);
}

/** The user closed a surface with no choice ("Later", "Not now", the close button). */
export function trackUpsellDismissed(facts: UpsellEventFacts): void {
  track(UPSELL_EVENTS.dismissed, facts);
}

/** A Stripe checkout starts. `surface` is where the offer came from. */
export function trackUpsellCheckoutStarted(
  facts: UpsellEventFacts & { tier: string; interval: 'monthly' | 'yearly' }
): void {
  SMLAnalytics.track('checkout_started', {
    kind: 'plan',
    tier: facts.tier,
    interval: facts.interval === 'yearly' ? 'year' : 'month',
    surface: facts.surface,
  }).catch(console.error);
}

/**
 * A plan checkout ended in a valid subscription (`PaymentCompleteView`). `upsell_surface` is the
 * upsell surface that started the checkout, or null.
 */
export function trackPurchaseCompleted(_data: {
  plan: Music24PlanTier;
  value?: number;
  currency?: string;
  upsell_surface: UpsellSurface | null;
}): void {
  SMLAnalytics.track('checkout_returned', { kind: 'plan', result: 'complete' }).catch(console.error);
  // The plan and the price come from the server (`revenue`, `subscription_started`).
}
